import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Package, Car, Users, ClipboardList, Truck, Calendar, Tv, ShoppingCart, AlertTriangle, Gauge, ArrowLeft, RefreshCw, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AtelierLayout = ({ children, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { path: '/atelier/planning', label: 'Planning atelier', icon: Calendar },
    { path: '/atelier/tv', label: 'Écran TV', icon: Tv },
    { path: '/atelier/gardiennage', label: 'Gardiennage pneus', icon: Car },
    { path: '/atelier/pneus-occasion', label: "Pneus d'occasion", icon: Car },
    { path: '/atelier/clients', label: 'Clients', icon: Users },
    { path: '/atelier/pointage', label: 'Timbreuse', icon: QrCode },
    { path: '/atelier/commandes', label: 'Commande stock', icon: ShoppingCart },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <div className="hidden md:flex md:w-56 md:flex-col bg-orange-950 text-white">
        <div className="p-5 border-b border-orange-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center shrink-0">
              <span className="text-white font-bold text-sm">V</span>
            </div>
            <span className="text-lg font-bold tracking-tight text-white">Atelier</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Poste de travail</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                variant="ghost"
                className={`w-full justify-start text-sm text-white hover:bg-orange-900 ${isActive ? 'bg-orange-900' : ''}`}
                data-testid={`atelier-nav-${item.path.split('/').pop()}`}
              >
                <Icon className="mr-3 w-4 h-4" />
                {item.label}
              </Button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-orange-800">
          <Button
            onClick={onLogout}
            variant="outline"
            className="w-full justify-start text-white border-orange-800 hover:bg-orange-900"
            data-testid="atelier-logout"
          >
            <LogOut className="mr-2 w-4 h-4" />
            Deconnexion
          </Button>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="md:hidden sticky top-0 z-10 bg-orange-950 border-b border-orange-800">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center">
                <span className="text-white font-bold text-xs">V</span>
              </div>
              <span className="text-sm font-bold text-white">Atelier</span>
            </div>
            <Button variant="ghost" size="sm" onClick={onLogout} className="text-slate-300">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
          <div className="px-4 pb-3 flex gap-2 overflow-x-auto">
            {navItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  size="sm"
                  variant={isActive ? 'default' : 'outline'}
                  className="whitespace-nowrap text-xs"
                >
                  <Icon className="mr-1 w-3 h-3" /> {item.label}
                </Button>
              );
            })}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {/* Top toolbar - Back & Refresh */}
          <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b border-slate-200 px-4 py-2 flex items-center justify-between">
            <button
              onClick={() => {
                if (window.history.length > 2) navigate(-1);
                else navigate('/atelier/planning');
              }}
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors px-2 py-1.5 rounded-lg hover:bg-slate-100"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Retour</span>
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors px-2 py-1.5 rounded-lg hover:bg-slate-100"
              data-testid="refresh-button"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default AtelierLayout;
